import React, { useEffect, useState } from "react";
import { Box, IconButton, Button, LinearProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ExcelIcon from "../../assets/excel-icon.svg";
import FileIcon from "../../assets/file-upload-icon.svg";
import Buttons from "../Controls/Buttons/Button";
import Label from "../../components/Label/Label";

const style = {
  position: "absolute",
  top: "58%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  border: "1px solid #4096FC",
  borderRadius: "8px",
  boxShadow: "none",
  p: 3,
};

export default function UploadModal({
  open,
  setOpen,
  onClose,
  handleUpload,
  data,
}) {
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);

  // Browse file upload
  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    addFiles(newFiles);
     event.target.value = null; 
  };

  // Drag & Drop upload
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (files.length > 0) return; // allow only 1 file

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      addFiles(droppedFiles);
      e.dataTransfer.clearData();
    }
  };

  // Add files with filter
  const addFiles = (incomingFiles) => {
    // if (files.length > 0) return; // only 1 file allowed

    const validFiles = incomingFiles.filter((file) =>
      [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
      ].includes(file.type)
    );

    const withMeta = validFiles.map((file) => ({
      file,
      progress: 0,
      uploaded: false,
    }));

    setFiles(withMeta);

    // simulate upload progress
    withMeta.forEach((f, index) => simulateUpload(f, index));
  };

  // Fake progress bar
  const simulateUpload = (fileObj, index) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setFiles((prev) =>
        prev.map((f, i) =>
          i === index ? { ...f, progress, uploaded: progress >= 100 } : f
        )
      );
      if (progress >= 100) clearInterval(interval);
    }, 100);
  };

  const handleDelete = (index) => {
    setFiles([]);
  };

  return (
    <div>
      <Box sx={style}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Label
            variant="subtitle1"
            fontWeight="bold"
            fontSize={18}
            text={"Upload Data"}
          />
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Sub text */}
        <Label
          variant="body2"
          sx={{ mt: 0.5, color: "text.secondary", pb: 2 }}
          fontSize={14}
          borderBottom="1px solid #e0e0e0"
          padding={"10px 0px"}
          text={"Upload your document here (maximum 1 file)"}
        />

        {/* Upload Box */}
        <Box
          sx={{
            border: dragActive ? "2px dashed #4096FC" : "1px dashed #90a4ae",
            borderRadius: "6px",
            mt: 2,
            p: 2,
            textAlign: "center",
            cursor: "pointer",
            transition: "0.2s",
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <img
            src={FileIcon}
            alt="upload"
            width={40}
            style={{ marginBottom: "8px" }}
          />
          <Label
            variant="body2"
            color="text.secondary"
            fontSize={14}
            padding={"5px 0px"}
            text={"Drag and drop your Excel/CSV file here"}
          />
          <Label variant="body2" color="text.secondary" padding={"5px 0px"} text={"OR"} />
          <Button variant="outlined" component="label">
            Browse File
            <input
              type="file"
              hidden
              accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileChange}
            />
          </Button>
        </Box>

        {/* Supported formats */}
        <Label
          variant="caption"
          display="block"
          fontSize={14}
          padding="5px 0px 0px 0px"
          textTransform="none"
          text={"Only supports .csv, .xls, .xlsx files"}
        />

        {/* File List */}
        <Box mt={2} maxHeight={100} overflow="auto">
          {files.map((f, i) => (
            <Box
              key={i}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "6px",
                p: 1,
                mb: 1,
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <img src={ExcelIcon} alt={"file"} />
                <Label
                  variant="body2"
                  text={`${f.file.name} (${(f.file.size / 1024 / 1024).toFixed(2)} MB)`}
                />
              </Box>
              <Box display="flex" alignItems="center" gap={1} minWidth="150px">
                {!f.uploaded ? (
                  <Box width="100%">
                    <LinearProgress variant="determinate" value={f.progress} />
                    <Label
                      variant="caption"
                      color="text.secondary"
                      text={`${f.progress}% Loading...`}
                    />
                  </Box>
                ) : (
                  <Label variant="caption" color="green" text={"Ready to upload"} />
                )}
                <IconButton onClick={() => handleDelete(i)} size="small">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Footer */}
        <Box display="flex" justifyContent="flex-end" gap={1} mt={3}>
          <Buttons colors="outline-gray" onClick={onClose} text="Cancel" />
          <Buttons text="Done" onClick={()=>handleUpload(files)} disabled={!files[0]} loading={data?.loader} />
        </Box>
      </Box>
    </div>
  );
}

